import React from 'react';
import { cn } from '../../../utils/cn';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Badge variant */
  variant?: 'default' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'primary';
  /** Badge size */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the badge should have rounded corners */
  rounded?: boolean;
}

const badgeVariants = {
  default: [
    'bg-primary-100 text-primary-800 border-primary-200',
    'dark:bg-primary-900 dark:text-primary-200 dark:border-primary-800',
  ],
  secondary: [
    'bg-neutral-100 text-neutral-800 border-neutral-200',
    'dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700',
  ],
  success: [
    'bg-success-50 text-success-700 border-success-200',
    'dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  ],
  warning: [
    'bg-warning-50 text-warning-700 border-warning-200',
    'dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  ],
  error: [
    'bg-error-50 text-error-700 border-error-200',
    'dark:bg-error-900/20 dark:text-error-300 dark:border-error-800',
  ],
  info: [
    'bg-info-50 text-info-700 border-info-200',
    'dark:bg-info-900/20 dark:text-info-300 dark:border-info-800',
  ],
};

const badgeSizes = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      rounded = false,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <span
        className={cn(
          // Base styles
          'inline-flex items-center font-medium border',
          'transition-colors duration-fast ease-luxury',
          // Variant styles
          badgeVariants[variant as keyof typeof badgeVariants],
          // Size styles
          badgeSizes[size],
          // Rounded corners
          rounded ? 'rounded-full' : 'rounded-md',
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';